import {
  State as AuthState,
  selectCurrentPatientGroup,
} from "../features/auth/authSlice";
import { Backdrop, Box, CircularProgress, SxProps, Theme } from "@mui/material";
import {
  CredentialResponse,
  GoogleLogin,
  GoogleOAuthProvider,
} from "@react-oauth/google";
import { useCallback, useEffect, useState } from "react";

import { AuthenticationResult, PublicClientApplication } from "@azure/msal-browser";
import { getMsalInstance, setMsalInstance } from "../msalService";
import { QueryStatus } from "@reduxjs/toolkit/query";
import React from "react";
import { createStyleSheet } from "../utils/styles";
import { getErrorText } from "../utils/errors";
import { useLazyGetRolesQuery } from "../features/api/apiSlice";
import { useLazyOpenIdAuthenticateQuery } from "../features/auth/authApiSlice";
import { useSelector } from "react-redux";
import MicrosoftSignInButton from "./MicrosoftSignInButton";

const TOKEN_AUTHORITY_GOOGLE = "accounts.google.com";
const TOKEN_AUTHORITY_MICROSOFT = "login.microsoftonline.com";

const LoginButton = (props: {
  googleClientId: string;
  microsoftClientId: string;
  onAuthSuccess: (authData: AuthState) => void;
  onAuthError: (errorMsg: string) => void;
  sx?: SxProps<Theme>;
}) => {
  const { googleClientId, microsoftClientId, onAuthSuccess, onAuthError, sx } = props;
  const patientGroup = useSelector(selectCurrentPatientGroup);

  // Component state
  const [isTryingToLogin, setIsTryingToLogin] = useState(false);

  // Queries/mutations
  const [openIdAuthenticate, openIdAuthenticateResult] =
    useLazyOpenIdAuthenticateQuery();
  const {
    data: authData,
    error: authError,
    isError: isAuthError,
    status: authStatus,
  } = openIdAuthenticateResult;
  const [getRoles, getRolesResult] = useLazyGetRolesQuery();
  const {
    data: rolesData,
    error: rolesDataError,
    isError: isRolesDataError,
    status: rolesDataStatus,
  } = getRolesResult;

  // Callbacks

  const handleCredential = useCallback(
    (credential: string, authority: string) => {
      setIsTryingToLogin(true);
      openIdAuthenticate({ credential, authority });
    },
    [openIdAuthenticate]
  );

  const loginErrorFactory = useCallback(
    (reason: string) => () => {
      setIsTryingToLogin(false);
      onAuthError(reason);
    },
    [onAuthError]
  );

  // Initialize MSAL once on mount and pick up any redirect result
  useEffect(() => {
    const instance = new PublicClientApplication({
      auth: {
        clientId: microsoftClientId,
        authority: "https://login.microsoftonline.com/common",
        redirectUri: window.location.origin,
      },
    });
    setMsalInstance(instance);
    instance.initialize().then(() => {
      instance.handleRedirectPromise().then((result: AuthenticationResult | null) => {
        if (result) handleCredential(result.idToken, TOKEN_AUTHORITY_MICROSOFT);
      }).catch((err: unknown) => onAuthError(`Microsoft login failed: ${err}`));
    });
  }, [microsoftClientId, handleCredential, onAuthError]);

  const handleMicrosoftLogin = useCallback(async () => {
    const msalInstance = getMsalInstance();
    if (!msalInstance) {
      onAuthError("Microsoft auth not ready, please try again");
      return;
    }
    try {
      await msalInstance.loginRedirect({
        scopes: ["openid", "email", "profile"],
      });
    } catch (err) {
      onAuthError(`Microsoft login failed: ${err}`);
    }
  }, [onAuthError]);

  // Reactive code
  useEffect(() => {
    if (authData && rolesDataStatus === QueryStatus.fulfilled) {
      const patientGroups = Object.keys(rolesData!.patient_groups!);
      let authorizedPatientGroup: string;
      if (
        patientGroup?.[authData.email] &&
        patientGroups.includes(patientGroup[authData.email])
      ) {
        authorizedPatientGroup = patientGroup[authData.email];
      } else {
        authorizedPatientGroup = patientGroups[0];
      }
      const newPatientGroup = {
        ...patientGroup,
        [authData.email]: authorizedPatientGroup,
      };
      setIsTryingToLogin(false);
      onAuthSuccess({
        ...authData,
        patientGroups,
        patientGroup: newPatientGroup,
      });
    } else if (isRolesDataError) {
      setIsTryingToLogin(false);
      onAuthError(getErrorText(rolesDataError));
    }
  }, [
    authData,
    rolesDataStatus,
    rolesData,
    isRolesDataError,
    rolesDataError,
    onAuthSuccess,
    onAuthError,
    patientGroup,
  ]);

  useEffect(() => {
    if (authStatus === QueryStatus.fulfilled) {
      getRoles(authData!.token!);
    } else if (isAuthError) {
      setIsTryingToLogin(false);
      onAuthError(getErrorText(authError));
    }
  }, [authStatus, authData, isAuthError, authError, getRoles, onAuthError]);

  return (
    <>
      <Box sx={[styles.buttonStack, sx] as SxProps<Theme>}>
        <GoogleOAuthProvider
          clientId={googleClientId}
          onScriptLoadError={loginErrorFactory("Failed to load Google API")}
        >
          <GoogleLogin
            onSuccess={(cr: CredentialResponse) =>
              handleCredential(cr.credential!, TOKEN_AUTHORITY_GOOGLE)
            }
            onError={loginErrorFactory("Google login failed")}
            size="large"
            theme="filled_blue"
          />
        </GoogleOAuthProvider>
        <MicrosoftSignInButton
          onClick={handleMicrosoftLogin}
          disabled={isTryingToLogin}
        />
      </Box>
      <Backdrop sx={styles.backdrop} open={isTryingToLogin}>
        <CircularProgress color="inherit" />
      </Backdrop>
    </>
  );
};

const styles = createStyleSheet({
  buttonStack: {
    display: "flex",
    flexDirection: "column",
    gap: "0.75rem",
  },
  backdrop: {
    color: "white",
    zIndex: (theme) => theme.zIndex.drawer + 1,
  },
});

export default LoginButton;
